<script lang="ts" setup>
import { getUploadMimeType, prepareImageUpload } from "~/utils/image-upload";
import ConfirmDialog from "primevue/confirmdialog";
import type { Checklist, Checklists } from "types/Checklist";
import { type SubmittedDocument } from "types/SubmittedDocument";
import { Assignment } from "~/classes/models/assignments/assignment.model";
import { Classroom } from "~/classes/models/classrooms/classroom.model";
import {
  Student,
  type Students,
} from "~/classes/models/students/student.model";

import { DocumentState } from "~/types/enums/DocumentState";

import {
  SubmissionJob,
  SubmissionJobType,
} from "~/classes/models/submission-job/submission-job.model";
import { DocumentSubmissionType } from "~/types/enums/DocumentSubmissionType.enum";

declare const google: any;

const props = defineProps({
  checklist: {
    type: Object as PropType<Checklist>,
    required: false,
  },
  checklistId: {
    type: String,
    required: false,
  },
  classrooms: {
    type: Array as PropType<Classroom[]>,
    required: false,
  },
  assignments: {
    type: Array as PropType<Assignment[]>,
    required: false,
  },
  classroom: {
    type: Object as PropType<Classroom>,
    required: false,
  },
  assignment: {
    type: Object as PropType<Assignment>,
    required: false,
  },
  checklists: {
    type: Array as PropType<Checklists>,
    required: false,
  },
  students: {
    type: Array as PropType<Students>,
    required: false,
  },
  student: {
    type: Object as PropType<Student>,
    required: false,
  },
  isGoogleImportEnabled: {
    type: Boolean,
    required: false,
    default: true,
  },
  showSamples: {
    type: Boolean,
    required: false,
    default: false,
  },
  attachChecklist: {
    type: Boolean,
    required: false,
    default: true,
  },
  documentSubmissionType: {
    type: String as PropType<DocumentSubmissionType>,
    required: false,
    default: () => DocumentSubmissionType.finalDraft,
  },
  submittedDocumentState: {
    type: String as PropType<DocumentState>,
    required: false,
    default: () => DocumentState.submitted,
  },
  acceptedFileTypes: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => [
      "application/vnd.google-apps.document",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
  },
  // Default computer and google drive
  acceptedUploadMethods: {
    type: Array as PropType<string[]>,
    required: false,
    default: () => ["computer", "google"],
  },
  popupPlacement: {
    type: String as PropType<"bottom" | "top" | "left" | "right">,
    default: "bottom",
  },
  popupAlignment: {
    type: String as PropType<"start" | "end" | "center">,
    default: "center",
  },
  canCreateFile: {
    type: Boolean,
    required: false,
    default: false,
  },
  writeOnly: {
    type: Boolean,
    required: false,
    default: false,
  },
  requiresReview: {
    type: Boolean,
    required: false,
    default: false,
  },
  parseBehavior: {
    type: String as PropType<"default" | "convertToHtml" | "convertToQuill">,
    required: false,
    default: () => "default",
  },
  autoOpen: {
    type: Boolean,
    required: false,
    default: true,
  },
  acceptMultiple: {
    type: Boolean,
    required: false,
    default: true,
  },
  enableStudentThesaurus: {
    type: Boolean,
    required: false,
    default: false,
  },
  enableStudentReview: {
    type: Boolean,
    required: false,
    default: false,
  },
  extractWritingCompanionChecklist: {
    type: Boolean,
    required: false,
    default: false,
  },
  showSubmissionWarning: {
    type: Boolean,
    required: false,
    default: false,
  },
  profileId: {
    type: String,
    required: false,
  },
});

const emit = defineEmits([
  "on-file-option-selected",
  "on-mobile-option-selected",
  "on-submission-job-created",
  "on-document-created",
]);

const appDataStore = useAppUserDataStore();
const { isPublicDemo } = storeToRefs(appDataStore);

const selectedChecklist = ref<Checklist | undefined>(props.checklist);
const selectedStudent = ref<Student | undefined>(props.student);
const selectedClassroom = ref<Classroom | undefined>(props.classroom);
const selectedAssignment = ref<Assignment | undefined>(props.assignment);

const { createDocument, updateDocumentStateById } = useDocuments();

const fileRef = ref<HTMLElement | undefined>();

const uploadFile = () => {
  if (isPublicDemo.value) {
    useBaseAlert("Oops!", "You can’t upload student submissions in demo mode.");
    return;
  }

  emit("on-file-option-selected");
  fileRef.value?.click();
};

const isUploading = ref(false);

// Every time we're uploading a file we'll add the document id to this array
// Once the api request finish processing we'll remove the id from the array
const filesBeingUploaded = ref<string[]>([]);

const files = ref<any[]>([]);

const dialogController = useNewDialogController();

const hasOnlyAcceptedFileTypes = (selectedFiles: { name: string; type?: string }[]) => {
  const hasUnsupportedFile = selectedFiles.some(
    (file) =>
      !useIsAcceptedFileType(getUploadMimeType(file), props.acceptedFileTypes)
  );

  if (!hasUnsupportedFile) return true;

  useBaseAlert(
    "Unsupported File Type",
    "One or more selected files aren't supported. Please choose a supported file type."
  );

  return false;
};

const onPickGoogle = () => {
  if (isPublicDemo.value) {
    useBaseAlert("Oops!", "You can’t upload student submissions in demo mode.");
    return;
  }

  emit("on-file-option-selected");
  signInToGoogle(); // User is not signed in, handle authentication
};

const accessToken = ref<any | undefined>(undefined);

const signInToGoogle = async () => {
  const tokenClientState = useState("tokenClient");

  // Request an access token.
  (tokenClientState.value as any).callback = async (response: any) => {
    if (response.error !== undefined) {
      throw response;
    }
    const accessToken = response.access_token;
    openGooglePicker(accessToken);
  };

  if (accessToken.value === null) {
    // Prompt the user to select a Google Account and ask for consent to share their data
    // when establishing a new session.
    (tokenClientState.value as any).requestAccessToken({ prompt: "consent" });
  } else {
    // Skip display of account chooser and consent dialog for an existing session.
    (tokenClientState.value as any).requestAccessToken({ prompt: "" });
  }
};

const openGooglePicker = (token: any) => {
  accessToken.value = token;

  const view = new google.picker.DocsView()
    .setIncludeFolders(true)
    .setSelectFolderEnabled(false)
    .setMimeTypes(props.acceptedFileTypes.join(","));

  const picker = new google.picker.PickerBuilder()
    .addView(view)
    .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
    .setOAuthToken(token)
    .setDeveloperKey(useRuntimeConfig().public.googleApiKey)
    .setCallback(pickerCallback)
    .setAppId(useRuntimeConfig().public.googleClientId)
    .build();
  picker.setVisible(true);
};

const pickerCallback = async (data: any) => {
  dialogController.value.close();

  if (data[google.picker.Response.ACTION] === google.picker.Action.PICKED) {
    const documents = data[google.picker.Response.DOCUMENTS];

    // Handle the selected file here

    const googleFiles = [] as any[];
    for (const doc of documents) {
      googleFiles.push({
        fileId: doc.id,
        name: doc.name,
        type: doc.mimeType,
      });
    }

    files.value = googleFiles;

    if (!hasOnlyAcceptedFileTypes(files.value)) return;

    var submissionJob = new SubmissionJob({
      autoOpen: props.autoOpen,
      requiresReview: props.requiresReview,
      userId:
        props.classroom?.userId ??
        props.student?.userId ??
        useCurrentUID() ??
        "",
      profileId: props.profileId,
      files: files.value,
      sheetController: useNewSheetController().value,
      uploadMethod: "google",
      documentSubmissionType: props.documentSubmissionType,
      submittedDocumentState: props.submittedDocumentState,
      attachChecklist: props.attachChecklist,
      accessToken: accessToken.value,
      id: useGenerateUID(),
      classrooms: props.classrooms,
      assignments: props.assignments,
      checklists: props.checklists,
      students: props.students,
      classroom: props.classroom,
      assignment: props.assignment,
      checklist: props.checklist,
      student: props.student,
      parseBehavior: props.parseBehavior,
      enableStudentThesaurus: props.enableStudentThesaurus,
      enableStudentReview: props.enableStudentReview,
      extractWritingCompanionChecklist:
        props.extractWritingCompanionChecklist,
    });

    if (props.checklists || props.students) {
      submissionJob.type = SubmissionJobType.additionalInformation;
      await useSubmissionJobs().addSubmissionJob(submissionJob);
      return;
    }
    // Check if all files are images
    const allImages = files.value.every((file) => {
      return getUploadMimeType(file).startsWith("image/");
    });

    if (allImages) {
      submissionJob.type = SubmissionJobType.additionalFiles;
      await useSubmissionJobs().addSubmissionJob(submissionJob);
      return;
    }

    isUploading.value = true;
    const documentIds = await submissionJob.uploadFiles();
    console.log("response from uploadFiles", documentIds);
    emit("on-document-created", documentIds);
    isUploading.value = false;
  }
};

const onFileSelected = async (ev: any) => {
  if (isUploading.value) return;
  files.value = [...ev.target.files];
  ev.target.files = null;
  ev.target.value = "";

  if (files.value.length == 0) {
    return;
  }

  if (!hasOnlyAcceptedFileTypes(files.value)) return;

  isUploading.value = true;
  try {
    const prepared = [];
    for (const file of files.value) {
      prepared.push(getUploadMimeType(file).startsWith("image/") ? await prepareImageUpload(file) : file);
    }
    files.value = prepared;
  } catch (error) {
    useBaseAlert("Could not prepare photos", error instanceof Error ? error.message : "Please choose another photo and try again.");
    return;
  } finally {
    isUploading.value = false;
  }

  dialogController.value.close();

  var submissionJob = new SubmissionJob({
    autoOpen: props.autoOpen,
    requiresReview: props.requiresReview,
    userId:
      props.classroom?.userId ?? props.student?.userId ?? useCurrentUID() ?? "",
    profileId: props.profileId,
    files: files.value,
    sheetController: useNewSheetController().value,
    uploadMethod: "computer",
    documentSubmissionType: props.documentSubmissionType,
    submittedDocumentState: props.submittedDocumentState,
    attachChecklist: props.attachChecklist,
    accessToken: accessToken.value,
    id: useGenerateUID(),
    classrooms: props.classrooms,
    assignments: props.assignments,
    checklists: props.checklists,
    students: props.students,
    classroom: props.classroom,
    assignment: props.assignment,
    checklist: props.checklist,
    student: props.student,
    parseBehavior: props.parseBehavior,
    enableStudentThesaurus: props.enableStudentThesaurus,
    enableStudentReview: props.enableStudentReview,
    extractWritingCompanionChecklist: props.extractWritingCompanionChecklist,
  });

  if (props.checklists || props.students) {
    submissionJob.type = SubmissionJobType.additionalInformation;
    await useSubmissionJobs().addSubmissionJob(submissionJob);
    return;
  }

  // Check if all files are images
  const allImages = files.value.every((file) => {
    return getUploadMimeType(file).startsWith("image/");
  });

  if (allImages) {
    submissionJob.type = SubmissionJobType.additionalFiles;
    await useSubmissionJobs().addSubmissionJob(submissionJob);
    return;
  }

  isUploading.value = true;
  const documentIds = await submissionJob.uploadFiles();
  emit("on-document-created", documentIds);
  isUploading.value = false;
};

const sampleDocumentsStore = useSampleDocuments();
const { sampleDocuments } = storeToRefs(sampleDocumentsStore);

const importSampleDocument = async (sampleDocument: SubmittedDocument) => {
  if (isPublicDemo.value) {
    useBaseAlert("Oops!", "You can’t upload student submissions in demo mode.");
    return;
  }

  const userId = useCurrentUID();

  if (!userId) return;

  if (!sampleDocument.id) return;

  await $fetch(`/api/documents/${sampleDocument.id}/sample/copy`, {
    headers: await useApiHeaders(),
  });
};

const allSamples = computed(() => {
  sampleDocuments.value.sort((a, b) => {
    return (a.sampleDisplayOrder ?? 0) - (b.sampleDisplayOrder ?? 0);
  });

  return sampleDocuments.value;
});

const firstSample = computed(() => {
  return allSamples.value[0];
});

const remaiingSamples = computed(() => {
  // return everythingf but the fist sample
  return allSamples.value.slice(1);
});

const acceptedFileTypeLabels = computed(() => {
  return props.acceptedFileTypes
    .map((fileType) => {
      switch (fileType) {
        case "application/vnd.google-apps.document":
          return "Google Doc";
        case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
          return "Word Document";
        case "application/pdf":
          return "PDF";
        case "image/*":
          return "Images";
        case "application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          return "Excel";
        case "application/vnd.google-apps.spreadsheet":
          return "Google Sheet";
        default:
          return "Unknown";
      }
    })
    .filter((label) => label != "Unknown");
});

const showMobileOption = computed(() => {
  return props.acceptedFileTypes.includes("image/*");
});

const openMobileSheet = async () => {
  emit("on-mobile-option-selected");
};
const createWritableFile = async () => {
  if (props.showSubmissionWarning == true) {
    const response = await useBaseConfirm(
      "Create New",
      "You have already started this assignment. Are you sure you want to start over?",
      false,
      "Create New",
      "Cancel"
    );

    if (response == false) {
      return;
    }
  }

  // First close the popup
  dialogController.value.close();

  let fileNameOptions = undefined as any;

  if (props.assignment && props.student) {
    fileNameOptions = {
      existingText: `${props.assignment.name} - ${props.student.name}`,
      okButtonLabel: "Create",
      cancelButtonLabel: "Cancel",
    };
  }

  const fileName = await useRenameModal("File Name", fileNameOptions);

  if (!fileName) return;

  var submissionJob = new SubmissionJob({
    autoOpen: props.autoOpen,
    requiresReview: props.requiresReview,
    userId:
      props.classroom?.userId ?? props.student?.userId ?? useCurrentUID() ?? "",
    profileId: props.profileId,
    files: [],
    sheetController: useNewSheetController().value,
    uploadMethod: "create",
    documentSubmissionType: props.documentSubmissionType,
    submittedDocumentState: props.submittedDocumentState,
    attachChecklist: props.attachChecklist,
    accessToken: accessToken.value,
    id: useGenerateUID(),
    classrooms: props.classrooms,
    assignments: props.assignments,
    checklists: props.checklists,
    students: props.students,
    classroom: props.classroom,
    assignment: props.assignment,
    checklist: props.checklist,
    student: props.student,
    parseBehavior: props.parseBehavior,
    enableStudentThesaurus: props.enableStudentThesaurus,
    enableStudentReview: props.enableStudentReview,
    extractWritingCompanionChecklist: props.extractWritingCompanionChecklist,
  });

  if (props.checklists || props.students) {
    submissionJob.type = SubmissionJobType.additionalInformation;
    useSubmissionJobs().addSubmissionJob(submissionJob);
  } else {
    isUploading.value = true;
    const documentId = await submissionJob.createWritableFile(fileName);
    emit("on-document-created", [documentId]);

    isUploading.value = false;
  }
};
</script>

<template>
  <div>
    <BasePopover
      v-if="
        acceptedUploadMethods.includes('computer') &&
        acceptedUploadMethods.includes('google')
      "
      :disabled="isGoogleImportEnabled != true || writeOnly == true"
      :side="popupPlacement"
      :align="popupAlignment"
      trigger-class="w-full"
      :controller="dialogController"
    >
      <div
        @click="writeOnly ? createWritableFile() : undefined"
        class="flex flex-row items-center whitespace-nowrap w-full"
      >
        <slot name="trigger" :is-uploading="isUploading">
          <BaseTextButton
            :show-spinner="isUploading"
            :class="{
              '!justify-start': true,
            }"
            class="w-full"
          >
            <div class="flex flex-row items-center whitespace-nowrap w-full">
              <ConfirmDialog group="upload">
                <template #message />
              </ConfirmDialog>

              <icon name="material-symbols:upload" size="20" class="shrink-0" />
              <span class="ml-2">
                <slot> Upload Student Submission </slot>
              </span>
            </div>
          </BaseTextButton>
        </slot>
      </div>
      <template #content>
        <div class="flex flex-col bg-surface p-1 rounded">
          <div class="border-b mb-1 pb-1">
            <div
              v-tooltip="acceptedFileTypeLabels.join(', ')"
              class="w-full p-2"
            >
              <div class="w-full flex flex-row items-center justify-left">
                <icon name="material-symbols:info-outline" size="20" />
                <span class="ml-2 text-[14px] leading-1 mt-[1px]">
                  Accepted File Types
                </span>
              </div>
            </div>
          </div>
          <BaseTextButton
            v-close-popper
            v-if="canCreateFile"
            class="w-full"
            @click="createWritableFile"
            color="default"
          >
            <div class="flex flex-row items-center w-full">
              <BaseIconFileEdit size="20" />
              <span class="ml-2"> Create </span>
            </div>
          </BaseTextButton>
          <BaseTextButton
            v-close-popper
            class="w-full"
            @click="uploadFile"
            color="default"
          >
            <div class="flex flex-row items-center w-full">
              <icon name="material-symbols:upload" size="20" />
              <span class="ml-2"> From Computer </span>
            </div>
          </BaseTextButton>
          <BaseTextButton
            v-close-popper
            class="w-full"
            @click="onPickGoogle"
            color="default"
          >
            <div class="flex flex-row items-center w-full">
              <icon name="mdi:google-drive" size="20" />

              <span class="ml-2"> From Google Drive </span>
            </div>
          </BaseTextButton>
          <BaseTextButton
            v-if="showMobileOption"
            v-close-popper
            class="w-full"
            color="default"
            @click="openMobileSheet"
          >
            <div class="flex flex-row items-center w-full">
              <BaseIconCamera size="20" />
              <span class="ml-2"> From Mobile Device </span>
            </div>
          </BaseTextButton>
          <BaseTextButton
            v-if="showSamples && firstSample"
            color="default"
            :key="firstSample.id"
            v-close-popper
            class="w-full"
            :data-step-id="`sample-document-button-${firstSample.name
              .toLowerCase()
              .split(' ')
              .join('-')}`"
            @click="importSampleDocument(firstSample)"
          >
            <div class="flex flex-row items-center w-full">
              <icon name="mi:document" size="20" />

              <span class="ml-2"> {{ firstSample.name }} </span>
            </div>
          </BaseTextButton>
          <BasePopover v-if="showSamples" side="right">
            <BaseTextButton color="default" class="w-full">
              <div class="flex flex-row items-center w-full">
                <icon name="material-symbols:folder-outline" size="20" />

                <span class="ml-2"> Samples </span>
              </div>
            </BaseTextButton>

            <template #content>
              <div class="flex flex-col p-1 rounded">
                <BaseTextButton
                  v-for="sample in remaiingSamples"
                  :key="sample.id"
                  v-close-popper
                  class="w-full"
                  :data-step-id="`sample-document-button-${sample.name
                    .toLowerCase()
                    .split(' ')
                    .join('-')}`"
                  @click="importSampleDocument(sample)"
                  color="default"
                >
                  <div class="flex flex-row items-center w-full">
                    <icon name="mi:document" size="20" />

                    <span class="ml-2"> {{ sample.name }} </span>
                  </div>
                </BaseTextButton>
              </div>
            </template>
          </BasePopover>
        </div>
      </template>
    </BasePopover>
    <div
      v-if="
        acceptedUploadMethods.length == 1 &&
        acceptedUploadMethods[0] == 'computer' &&
        writeOnly == false
      "
      @click="uploadFile"
    >
      <slot name="trigger" :is-uploading="isUploading">
        <BaseTextButton
          :show-spinner="isUploading"
          class="w-full"
          color="default"
        >
          <div class="flex flex-row items-center w-full">
            <icon name="material-symbols:upload" size="20" />
          </div>
          <span class="ml-2"> From Computer </span>
        </BaseTextButton>
      </slot>
    </div>
    <div
      v-if="
        acceptedUploadMethods.length == 1 &&
        acceptedUploadMethods[0] == 'google' &&
        writeOnly == false
      "
      @click="onPickGoogle"
    >
      <slot name="trigger" :is-uploading="isUploading">
        <BaseTextButton
          :show-spinner="isUploading"
          class="w-full"
          color="default"
        >
          <div class="flex flex-row items-center w-full">
            <icon name="material-symbols:upload" size="20" />
          </div>
          <span class="ml-2"> From Google Drive </span>
        </BaseTextButton>
      </slot>
    </div>
    <input
      ref="fileRef"
      class="hidden"
      type="file"
      :accept="acceptedFileTypes.join(',')"
      :multiple="acceptMultiple"
      @change="onFileSelected"
    />
  </div>
</template>

<style scoped></style>
